<div class="palette-selector-dialog">
    <!-- Header -->
    <div class="section-header" mat-dialog-title>
        <mat-icon class="section-icon">palette</mat-icon>
        <span class="section-title">{{ 'ai.colorPaletteSelector' | transloco }}</span>
        <button mat-icon-button class="close-button" mat-dialog-close>
            <mat-icon>close</mat-icon>
        </button>
    </div>

    <!-- Main Content -->
    <div mat-dialog-content class="dialog-content">
        <!-- Mobile Dropdowns -->
        @if (isMobileView) {
            <div class="mobile-controls">
                <!-- Category Filter -->
                <mat-form-field appearance="outline" class="category-dropdown">
                    <mat-label>
                        <mat-icon>folder</mat-icon>
                        {{ 'ai.category' | transloco }}
                    </mat-label>
                    <mat-select [(value)]="selectedCategory" (selectionChange)="selectCategory($event.value)">
                        <mat-option [value]="null">
                            <mat-icon>apps</mat-icon>
                            {{ 'ai.allPalettes' | transloco }}
                        </mat-option>
                        @for (cat of filteredCategoryList; track cat.name) {
                            <mat-option [value]="cat.name">
                                <mat-icon>folder</mat-icon>
                                {{ cat.name }} ({{ cat.count }})
                            </mat-option>
                        }
                    </mat-select>
                </mat-form-field>

                <!-- Palette Selector -->
                <mat-form-field appearance="outline" class="palette-dropdown">
                    <mat-label>
                        <mat-icon>palette</mat-icon>
                        {{ 'ai.selectPalette' | transloco }}
                    </mat-label>
                    <mat-select [(value)]="selectedPalette" (selectionChange)="selectPalette($event.value)">
                        @if (filteredPalettes.length === 0) {
                            <mat-option disabled>
                                {{ 'ai.noPalettesFound' | transloco }}
                            </mat-option>
                        }
                        @for (palette of filteredPalettes; track palette.name) {
                            <mat-option [value]="palette">
                                <div class="palette-option">
                                    <span class="palette-option-name">{{ palette.name }}</span>
                                    <div class="palette-option-colors">
                                        <div class="mini-swatch" [style.background-color]="palette.primaryAccent"></div>
                                        @for (color of palette.secondaryAccents.slice(0, 3); track color) {
                                            <div class="mini-swatch" [style.background-color]="color"></div>
                                        }
                                    </div>
                                </div>
                            </mat-option>
                        }
                    </mat-select>
                </mat-form-field>
            </div>
        }

        <div class="content-wrapper">
            <!-- Left Sidebar - Category Tree (Hidden on Mobile) -->
            <div class="sidebar" [class.mobile-hidden]="isMobileView">
                <div class="search-box">
                    <mat-form-field appearance="outline" class="search-field">
                        <mat-icon matPrefix>search</mat-icon>
                        <input matInput
                               [(ngModel)]="searchQuery"
                               (ngModelChange)="onSearchChange()"
                               [placeholder]="'ai.searchPalettes' | transloco">
                        @if (searchQuery) {
                            <button mat-icon-button matSuffix (click)="clearSearch()">
                                <mat-icon>close</mat-icon>
                            </button>
                        }
                    </mat-form-field>
                </div>

                <div class="category-list">
                    <!-- Show All Option -->
                    <div class="category-item"
                         [class.selected]="selectedCategory === null"
                         (click)="selectCategory(null)">
                        <mat-icon class="category-icon">apps</mat-icon>
                        <span class="category-name">{{ 'ai.allPalettes' | transloco }}</span>
                    </div>

                    <!-- Category Items (filtered) -->
                    @for (cat of filteredCategoryList; track cat.name) {
                        <div class="category-item"
                             [class.selected]="cat.name === selectedCategory"
                             (click)="selectCategory(cat.name)">
                            <mat-icon class="category-icon">folder</mat-icon>
                            <span class="category-name">{{ cat.name }}</span>
                        </div>
                    }
                </div>
            </div>

            <!-- Right Content - Palettes Grid and Preview -->
            <div class="main-content">
                <!-- Palettes Grid (Hidden on Mobile) -->
                <div class="palettes-grid"
                     #palettesGrid
                     [class.mobile-hidden]="isMobileView"
                     [style.height.%]="showPreview && !isMobileView ? gridHeight : null">
                    @if (filteredPalettes.length === 0) {
                        <div class="no-results">
                            <mat-icon>palette</mat-icon>
                            <p>{{ 'ai.noPalettesFound' | transloco }}</p>
                        </div>
                    }
                    @for (palette of filteredPalettes; track palette.name) {
                        <div class="palette-card"
                             [class.selected]="selectedPalette?.name === palette.name"
                             (click)="selectPalette(palette); $event.stopPropagation()">
                            <div class="palette-header">
                                <h4 class="palette-name">{{ palette.name }}</h4>
                                <span class="palette-category">{{ palette.category }}</span>
                            </div>
                        </div>
                    }
                </div>

                <!-- Resize Handle (Only show when preview is visible and not mobile) -->
                @if (selectedPalette && showPreview && !isMobileView) {
                    <div class="resize-handle"
                         (mousedown)="onResizeStart($event)">
                        <mat-icon class="resize-handle-icon">drag_indicator</mat-icon>
                        <mat-icon class="resize-handle-icon">drag_indicator</mat-icon>
                    </div>
                }

                <!-- Preview Section (Only show when showPreview is true) -->
                @if (selectedPalette && showPreview) {
                    <div class="preview-section"
                         [class.mobile-hidden]="isMobileView"
                         [style.height.%]="!isMobileView ? previewHeight : null">
                        <div class="preview-header">
                            <mat-icon>visibility</mat-icon>
                            <h3>{{ 'ai.preview' | transloco }}: {{ selectedPalette.name }}</h3>
                        </div>
                        <div class="preview-content"
                             [style.background-color]="selectedPalette.pageBackground"
                             [class.page-bg-pulse]="clickedColorType === 'pageBackground'"
                             (mouseenter)="setHoveredColor('pageBackground')"
                             (mouseleave)="clearHoveredColor()">
                            <!-- Realistic Scene Preview -->
                            <div class="preview-card"
                                 [style.background-color]="selectedPalette.cardBackground"
                                 [class.pulse]="clickedColorType === 'cardBackground'"
                                 (mouseenter)="setHoveredColor('cardBackground')"
                                 (mouseleave)="clearHoveredColor()">
                                <!-- Large Icon Preview (star 150x150) -->
                                <div class="preview-icon-container"
                                     [class.pulse]="clickedColorType === 'headingColor'"
                                     (mouseenter)="setHoveredColor('headingColor')"
                                     (mouseleave)="clearHoveredColor()">
                                    <mat-icon class="preview-icon-large"
                                              [style.color]="selectedPalette.headingColor">
                                        star
                                    </mat-icon>
                                </div>

                                <!-- Header | Title | Label with secondary accents -->
                                <div class="preview-header-types">
                                    <span class="header-type-text"
                                          [style.color]="selectedPalette.secondaryAccents[0]"
                                          [class.pulse]="clickedColorType === 'secondaryAccent0'"
                                          (mouseenter)="setHoveredColor('secondaryAccent0')"
                                          (mouseleave)="clearHoveredColor()">
                                        {{ 'ai.sampleHeader' | transloco }}
                                    </span>
                                    <span class="separator">|</span>
                                    <span class="header-type-text"
                                          [style.color]="selectedPalette.secondaryAccents[1]"
                                          [class.pulse]="clickedColorType === 'secondaryAccent1'"
                                          (mouseenter)="setHoveredColor('secondaryAccent1')"
                                          (mouseleave)="clearHoveredColor()">
                                        {{ 'ai.sampleTitle' | transloco }}
                                    </span>
                                    <span class="separator">|</span>
                                    <span class="header-type-text"
                                          [style.color]="selectedPalette.secondaryAccents[2]"
                                          [class.pulse]="clickedColorType === 'secondaryAccent2'"
                                          (mouseenter)="setHoveredColor('secondaryAccent2')"
                                          (mouseleave)="clearHoveredColor()">
                                        {{ 'ai.sampleLabel' | transloco }}
                                    </span>
                                </div>

                                <!-- Clock Component Preview -->
                                <div class="preview-clock"
                                     [class.pulse]="clickedColorType === 'componentColor'"
                                     (mouseenter)="setHoveredColor('componentColor')"
                                     (mouseleave)="clearHoveredColor()">
                                    <mat-icon [style.color]="selectedPalette.componentColor">schedule</mat-icon>
                                    <span class="clock-text"
                                          [style.color]="selectedPalette.componentColor">
                                        {{ 'ai.sampleTime' | transloco }}
                                    </span>
                                </div>

                                <!-- Heading with headingColor -->
                                <h2 class="preview-heading"
                                    [style.color]="selectedPalette.headingColor"
                                    [class.pulse]="clickedColorType === 'headingColor'"
                                    (mouseenter)="setHoveredColor('headingColor')"
                                    (mouseleave)="clearHoveredColor()">
                                    {{ 'ai.sampleHeading' | transloco }}
                                </h2>

                                <!-- Label/Subtitle with primaryAccent -->
                                <div class="preview-label"
                                     [style.color]="selectedPalette.primaryAccent"
                                     [class.pulse]="clickedColorType === 'primaryAccent'"
                                     (mouseenter)="setHoveredColor('primaryAccent')"
                                     (mouseleave)="clearHoveredColor()">
                                    {{ 'ai.sampleLabelComponent' | transloco }}
                                </div>

                                <!-- Body text with componentColor -->
                                <p class="preview-body"
                                   [style.color]="selectedPalette.componentColor"
                                   [class.pulse]="clickedColorType === 'componentColor'"
                                   (mouseenter)="setHoveredColor('componentColor')"
                                   (mouseleave)="clearHoveredColor()">
                                    {{ 'ai.sampleBodyText' | transloco }}
                                </p>

                                <!-- Angular Material Button -->
                                <button mat-raised-button
                                        class="preview-button"
                                        [class.pulse]="clickedColorType === 'primaryAccent'"
                                        [style.background-color]="selectedPalette.primaryAccent"
                                        [style.color]="'#ffffff'"
                                        (mouseenter)="setHoveredColor('primaryAccent')"
                                        (mouseleave)="clearHoveredColor()">
                                    <mat-icon>check_circle</mat-icon>
                                    {{ 'ai.sampleButton' | transloco }}
                                </button>
                            </div>

                            <!-- Color Details Reference -->
                            <div class="color-details">
                                <div class="color-item"
                                     [class.highlighted]="hoveredColorType === 'primaryAccent'"
                                     (mouseenter)="onColorClick('primaryAccent')"
                                     (mouseleave)="clickedColorType = null">
                                    <div class="color-box" [style.background-color]="selectedPalette.primaryAccent"></div>
                                    <div class="color-info">
                                        <span class="color-label">{{ 'ai.primaryAccent' | transloco }}</span>
                                        <span class="color-value">{{ selectedPalette.primaryAccent }}</span>
                                    </div>
                                </div>
                                @for (color of selectedPalette.secondaryAccents; track $index) {
                                    <div class="color-item"
                                         [class.highlighted]="hoveredColorType === 'secondaryAccent' + $index"
                                         (mouseenter)="onColorClick('secondaryAccent' + $index)"
                                         (mouseleave)="clickedColorType = null">
                                        <div class="color-box" [style.background-color]="color"></div>
                                        <div class="color-info">
                                            <span class="color-label">{{ 'ai.secondaryAccent' | transloco }} {{ $index + 1 }}</span>
                                            <span class="color-value">{{ color }}</span>
                                        </div>
                                    </div>
                                }
                                <div class="color-item"
                                     [class.highlighted]="hoveredColorType === 'headingColor'"
                                     (mouseenter)="onColorClick('headingColor')"
                                     (mouseleave)="clickedColorType = null">
                                    <div class="color-box" [style.background-color]="selectedPalette.headingColor"></div>
                                    <div class="color-info">
                                        <span class="color-label">{{ 'ai.headingColor' | transloco }}</span>
                                        <span class="color-value">{{ selectedPalette.headingColor }}</span>
                                    </div>
                                </div>
                                <div class="color-item"
                                     [class.highlighted]="hoveredColorType === 'componentColor'"
                                     (mouseenter)="onColorClick('componentColor')"
                                     (mouseleave)="clickedColorType = null">
                                    <div class="color-box" [style.background-color]="selectedPalette.componentColor"></div>
                                    <div class="color-info">
                                        <span class="color-label">{{ 'ai.componentColor' | transloco }}</span>
                                        <span class="color-value">{{ selectedPalette.componentColor }}</span>
                                    </div>
                                </div>
                                <div class="color-item"
                                     [class.highlighted]="hoveredColorType === 'pageBackground'"
                                     (mouseenter)="onColorClick('pageBackground')"
                                     (mouseleave)="clickedColorType = null">
                                    <div class="color-box" [style.background-color]="selectedPalette.pageBackground"></div>
                                    <div class="color-info">
                                        <span class="color-label">{{ 'ai.pageBackground' | transloco }}</span>
                                        <span class="color-value">{{ selectedPalette.pageBackground }}</span>
                                    </div>
                                </div>
                                <div class="color-item"
                                     [class.highlighted]="hoveredColorType === 'cardBackground'"
                                     (mouseenter)="onColorClick('cardBackground')"
                                     (mouseleave)="clickedColorType = null">
                                    <div class="color-box" [style.background-color]="selectedPalette.cardBackground"></div>
                                    <div class="color-info">
                                        <span class="color-label">{{ 'ai.cardBackground' | transloco }}</span>
                                        <span class="color-value">{{ selectedPalette.cardBackground }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                }
            </div>
        </div>
    </div>

    <!-- Footer Actions -->
    <div mat-dialog-actions class="dialog-actions">
        <button mat-button (click)="getRandomPalette()" class="random-button">
            <mat-icon>shuffle</mat-icon>
            {{ 'ai.randomPalette' | transloco }}
        </button>
        <div class="action-buttons">
            <button mat-button (click)="cancel()">
                {{ 'general.cancel' | transloco }}
            </button>
            <button mat-raised-button
                    color="warn"
                    (click)="applyPalette()"
                    [disabled]="!selectedPalette">
                <mat-icon>check</mat-icon>
                {{ 'ai.applyPalette' | transloco }}
            </button>
        </div>
    </div>
</div>
